import { forwardRef, useEffect, useRef, useState } from 'react';

import clsx from 'clsx';
import { fabric } from 'fabric';

import { BaseCanvas } from '@/components/common';
import { Spinner } from '@/components/generic';
import { CANVAS_CONTAINER_ID, DEFAULT_CANVAS_JSON } from '@/constants';
import { getCanvasResizeScale, resizeCanvas } from '@/helpers';

type TextOverlayCanvasProps = {
  contentJson: string;
  onCanvasLoad?: (loadedCanvas: fabric.Canvas) => void;
};

const TextOverlayCanvas = forwardRef<fabric.Canvas | null, TextOverlayCanvasProps>(
  ({ contentJson, onCanvasLoad }, ref) => {
    const canvasRef = useRef<fabric.Canvas | null>(null);
    const baseCanvasSizeRef = useRef<{ width: number; height: number } | null>({
      width: DEFAULT_CANVAS_JSON.width,
      height: DEFAULT_CANVAS_JSON.height
    });
    const [isCanvasInitialized, setIsCanvasInitialized] = useState(false);

    const loadCanvas = (canvasJson: string) => {
      const canvas = canvasRef.current;

      if (!canvas) {
        return;
      }

      const parsedContentJson = JSON.parse(canvasJson);

      canvas.loadFromJSON(parsedContentJson, () => {
        const { width, height } = DEFAULT_CANVAS_JSON;
        const scale = getCanvasResizeScale(CANVAS_CONTAINER_ID, width, height);

        baseCanvasSizeRef.current = { width, height };

        resizeCanvas(canvas, scale, width, height);
        canvas.renderAll();
        onCanvasLoad?.(canvas);
        setIsCanvasInitialized(true);
      });
    };
    const handleCanvasInit = (canvas: fabric.Canvas) => {
      canvasRef.current = canvas;

      if (!ref) {
        return;
      }

      if (typeof ref === 'function') {
        ref(canvas);
        return;
      }

      ref.current = canvas;
    };

    useEffect(() => {
      loadCanvas(contentJson);
    }, [contentJson]);

    return (
      <>
        <BaseCanvas
          containerId={CANVAS_CONTAINER_ID}
          containerClassName={clsx(
            'flex h-full flex-col justify-center transition-opacity',
            isCanvasInitialized ? 'opacity-100' : 'opacity-0'
          )}
          canvasClassName='block aspect-video size-full rounded-2xl border border-blue-powder'
          baseCanvasSizeRef={baseCanvasSizeRef}
          onCanvasInit={handleCanvasInit}
        />

        {!isCanvasInitialized && (
          <div className='absolute left-0 top-0 size-full'>
            <Spinner />
          </div>
        )}
      </>
    );
  }
);

TextOverlayCanvas.displayName = 'TextOverlayCanvas';

export { TextOverlayCanvas };

